import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Leaf } from "lucide-react";
import { LineReveal } from "./Reveal";
import { IMAGES, PRODUCTS, TAGLINE } from "../../data/content";

export const Hero = ({ onScrollTo }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const blobY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden pt-28 md:pt-24"
    >
      {/* floating parallax blob */}
      <motion.img
        style={{ y: blobY }}
        src={IMAGES.heroTexture}
        alt=""
        aria-hidden
        className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full object-cover opacity-40 blur-[2px]"
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
        {/* Left: kinetic type */}
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="flex items-center gap-2 eyebrow text-brand mb-6"
          >
            <Leaf size={14} className="text-accent" />
            {TAGLINE}
          </motion.div>

          <h1 className="font-serif font-light text-ink leading-[0.86] tracking-tight text-[3.4rem] sm:text-7xl lg:text-[7.2rem]">
            <LineReveal lines={["Pies suaves,", "renovados"]} delay={0.3} />
            <span className="line-mask">
              <motion.span
                className="block italic text-accent"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.66 }}
              >
                por naturaleza
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-8 max-w-md text-base md:text-lg text-ink/70 leading-relaxed"
          >
            Hydra Power es la crema hidratante botánica que repara, suaviza y
            refresca tus pies con un ritual de spa diario en casa.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-offers"
              onClick={() => onScrollTo("#ofertas")}
              className="group inline-flex items-center gap-3 rounded-full bg-brand text-white pl-7 pr-3 py-3 text-sm hover:bg-accent transition-colors duration-300"
            >
              Ver ofertas
              <span className="grid place-items-center h-9 w-9 rounded-full bg-white/15 group-hover:rotate-45 transition-transform duration-300">
                <ArrowDownRight size={18} />
              </span>
            </button>
            <button
              data-testid="hero-cta-solutions"
              onClick={() => onScrollTo("#soluciones")}
              className="text-sm text-ink/70 underline underline-offset-4 hover:text-accent transition-colors"
            >
              ¿Cómo funciona?
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="mt-14 flex items-center gap-8 border-t border-line/70 pt-6 max-w-md"
          >
            <div>
              <div className="font-serif text-3xl text-ink">+12k</div>
              <div className="text-xs text-ink/60 mt-1">Clientes felices</div>
            </div>
            <div>
              <div className="font-serif text-3xl text-ink">100%</div>
              <div className="text-xs text-ink/60 mt-1">Botánico</div>
            </div>
            <div>
              <div className="font-serif text-3xl text-ink">4.9★</div>
              <div className="text-xs text-ink/60 mt-1">Valoración media</div>
            </div>
          </motion.div>
        </motion.div>

        {/* Right: product spotlight */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.5 }}
            className="relative w-full max-w-[440px] mx-auto"
          >
            <motion.img
              style={{ y: imgY }}
              src={PRODUCTS.duo}
              alt="Hydra Power crema hidratante para pies"
              className="w-full h-auto drop-shadow-[0_40px_60px_rgba(26,37,27,0.35)]"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.2, duration: 0.7 }}
            className="absolute -left-2 bottom-10 bg-surface rounded-2xl px-5 py-4 shadow-[0_12px_40px_rgba(58,75,60,0.15)] border border-line"
          >
            <div className="eyebrow text-accent">Desde</div>
            <div className="font-serif text-3xl text-ink leading-none">
              $59.000 <span className="text-sm font-body text-ink/50">COP</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
